import React, { useEffect, useState } from "react";
import { db } from "../../config/firebaseConfig";
import { ref, get } from "firebase/database";

interface Advantage {
  title: string;
  content: string;
}

interface AdvantagesData {
  intro: string;
  advantages: Advantage[];
}

const Advantages: React.FC = () => {
  const [advantagesData, setAdvantagesData] = useState<AdvantagesData>({
    intro: "",
    advantages: [],
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Scroll to the top when component mounts
    const aboutElement = document.getElementById("topbartopscroll");
    if (aboutElement) {
      aboutElement.scrollIntoView({ behavior: "smooth" });
    }

    // Fetch advantages data from Firebase
    const fetchAdvantagesData = async () => {
      try {
        const advantagesRef = ref(db, "LayoutSettingData/Advantages");
        const snapshot = await get(advantagesRef);

        if (snapshot.exists()) {
          setAdvantagesData(snapshot.val());
        } else {
          // If no data exists, use default content
          const defaultData: AdvantagesData = {
            intro:
              "At Afrocart, we pride ourselves on offering numerous advantages to enhance your shopping and catering experience. Our platform is designed to cater to your needs with efficiency and excellence.",
            advantages: [
              {
                title: "Convenience",
                content:
                  "With our easy-to-use online platform, you can shop for groceries and catering supplies from the comfort of your home or office. Our streamlined ordering process and fast delivery ensure a hassle-free experience.",
              },
              {
                title: "Quality Assurance",
                content:
                  "We ensure that all products meet high standards of quality. From fresh groceries to catering supplies, we carefully select and vet our products to guarantee the best for our customers.",
              },
              {
                title: "Competitive Pricing",
                content:
                  "Our pricing is designed to be competitive, providing value for money without compromising on quality. Enjoy the best prices on a wide range of products and services.",
              },
              {
                title: "Tailored Services",
                content:
                  "We offer tailored services for catering businesses, ensuring that your specific needs are met. Our platform is equipped to handle bulk orders and customized requests efficiently.",
              },
              {
                title: "Customer Support",
                content:
                  "Our dedicated customer support team is available to assist you with any queries or issues. We are committed to providing prompt and helpful support to enhance your experience with Afrocart.",
              },
            ],
          };
          setAdvantagesData(defaultData);
        }
      } catch (error) {
        console.error("Error fetching advantages data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAdvantagesData();
  }, []);

  if (loading) {
    return (
      <div className="p-4 md:px-[15vw] md:pt-[5vh] w-full">
        {/* Title skeleton */}
        <div className="h-16 w-3/4 bg-gray-200 animate-pulse rounded-md mb-8"></div>

        {/* Intro text skeleton */}
        <div className="h-6 w-full bg-gray-200 animate-pulse rounded-md mb-8"></div>

        {/* Advantages list skeleton */}
        <div className="space-y-6">
          {[1, 2, 3, 4, 5].map((item) => (
            <div key={item} className="space-y-2">
              {/* Advantage title skeleton */}
              <div className="h-6 w-1/4 bg-gray-200 animate-pulse rounded-md"></div>
              {/* Advantage content skeleton */}
              <div className="h-16 w-full bg-gray-200 animate-pulse rounded-md"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="p-4 md:px-[15vw] md:pt-[5vh] w-full flex flex-col font-normal text-sm space-y-5 text-black">
        <div>
          {/* <p className="flex items-start text-[1.65vh] text-customGrayBase">
            Last updated: 10/22/2024
          </p> */}
          <h1 className="mt-[4%] font-helveticaBold text-[5.5vh] xxl:text-[7.5vh] leading-none text-black">
            Advantages of Afrocart
          </h1>
        </div>

        <p className="font-helvetica">
          At <span className="font-helveticaBold">Afrocart</span>,{" "}
          {advantagesData.intro.substring(
            advantagesData.intro.indexOf(",") + 1
          )}
        </p>

        <ol className="decimal_ol space-y-4 px-4">
          {advantagesData.advantages.map((advantage, index) => (
            <li key={index} className="font-helveticaBold text-black">
              {advantage.title}
              <p className="font-helvetica text-black">{advantage.content}</p>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
};

export default Advantages;
